import {
    IBalusterGeometry,
    IHandrailGeometry,
    IRailingConfig,
    IRailingGeometry,
    IStairConfig,
    IStepGeometry,
    IVec3,
} from '@stair-craft/common/util';

import { balusterCount, treadBalusterRuns } from '../shared-baluster-count/baluster-count';
import { railLineOffset } from '../shared-board-band/board-band';

/** Where the slab lies around the opening: past the exit edge and past each side edge. */
interface ISlabSides {
    readonly pastExit: boolean;
    readonly left: boolean;
    readonly right: boolean;
}

/** What one side's railing is built from. */
interface ISideInput {
    readonly steps: IStepGeometry[];
    readonly config: IStairConfig;
    readonly side: 'left' | 'right';
    readonly slab: ISlabSides;
}

/** Balusters along the flight, the same number on every tread, between the post faces. */
function flightBalusters(input: ISideInput, x: number, bottom: IVec3, top: IVec3): IBalusterGeometry[] {
    const rc: IRailingConfig = input.config.railing;
    const steps: IStepGeometry[] = input.steps;
    const flight: { stringerType: IStairConfig['stringerType']; axis: 'z' } = { stringerType: input.config.stringerType, axis: 'z' };
    const runs: number[] = treadBalusterRuns(bottom.z + rc.postWidth / 2, top.z - rc.postWidth / 2, steps, flight, rc);
    const balusters: IBalusterGeometry[] = [];
    for (const z of runs) {
        const stepIndex: number = Math.min(Math.floor((z - steps[0].position.z) / steps[0].treadDepth), steps.length - 1);
        const baseY: number = steps[stepIndex].position.y;
        const railY: number = bottom.y + ((z - bottom.z) / (top.z - bottom.z)) * (top.y - bottom.y);
        balusters.push({ position: { x, z, y: baseY }, height: Math.max(rc.height / 2, railY - baseY), rotation: 0 });
    }
    return balusters;
}

/**
 * The run along the exit edge, on the slab, out to the corner post of the opening-side railing;
 * null where that side has no slab or the run would be shorter than a post.
 */
function exitConnection(input: ISideInput, x: number, top: IVec3): { end: IVec3; balusters: IBalusterGeometry[] } | null {
    const rc: IRailingConfig = input.config.railing;
    const outward: number = input.side === 'left' ? -1 : 1;
    const openingEdgeX: number = input.side === 'left' ? 0 : input.config.openingWidth;
    const cornerX: number = openingEdgeX + (outward * rc.postWidth) / 2;
    const slabBeside: boolean = input.side === 'left' ? input.slab.left : input.slab.right;
    if (!input.slab.pastExit || !slabBeside || Math.abs(cornerX - x) <= rc.postWidth + rc.balusterWidth) {
        return null;
    }
    const fromX: number = x + (outward * rc.postWidth) / 2;
    const toX: number = cornerX - (outward * rc.postWidth) / 2;
    const count: number = balusterCount(Math.abs(toX - fromX), rc);
    const slabTop: number = input.config.floorHeight + input.config.floorThickness;
    const balusters: IBalusterGeometry[] = [];
    for (let i: number = 0; i < count; i++) {
        const bx: number = fromX + ((i + 0.5) / count) * (toX - fromX);
        balusters.push({ position: { x: bx, y: slabTop, z: top.z }, height: rc.height, rotation: 0 });
    }
    return { balusters, end: { x: cornerX, y: top.y, z: top.z } };
}

/**
 * One side's railing. The top post stands on the slab past the exit, its face flush with the
 * exit edge; where the opening reaches the room wall there is no slab there, and it stands on
 * the top tread instead.
 */
function buildSideRailing(input: ISideInput): IRailingGeometry {
    const { steps, config, side, slab } = input;
    const rc: IRailingConfig = config.railing;
    const slabTop: number = config.floorHeight + config.floorThickness;
    const edgeOffset: number = railLineOffset(config.stringerType, rc);
    const x: number = side === 'left' ? edgeOffset : config.stairWidth - edgeOffset;
    const firstStep: IStepGeometry = steps[0];
    const lastStep: IStepGeometry = steps[steps.length - 1];
    const exitZ: number = lastStep.position.z + lastStep.treadDepth;
    const topBase: number = slab.pastExit ? slabTop : lastStep.position.y;

    const bottomPost: IBalusterGeometry = {
        position: { x, y: 0, z: firstStep.position.z },
        height: firstStep.position.y + rc.height,
        rotation: 0,
    };
    const topPost: IBalusterGeometry = {
        position: { x, y: topBase, z: slab.pastExit ? exitZ + rc.postWidth / 2 : exitZ - rc.postWidth / 2 },
        height: slabTop + rc.height - topBase,
        rotation: 0,
    };
    const bottom: IVec3 = { x, y: bottomPost.height, z: bottomPost.position.z };
    const top: IVec3 = { x, y: slabTop + rc.height, z: topPost.position.z };
    const connection: { end: IVec3; balusters: IBalusterGeometry[] } | null = exitConnection(input, x, top);
    const handrail: IHandrailGeometry = {
        points: connection ? [bottom, top, connection.end] : [bottom, top],
        width: rc.handrailWidth,
        height: rc.handrailHeight,
    };
    return {
        side,
        handrail,
        balusters: [...flightBalusters(input, x, bottom, top), ...(connection?.balusters ?? [])],
        posts: [bottomPost, topPost],
        balusterWidth: rc.balusterWidth,
        postWidth: rc.postWidth,
    };
}

/**
 * Stair-following railings for a straight flight: a bottom and a top post per side, a sloped
 * handrail, and — where the slab lies past the exit and beside the opening — a level run along
 * the exit edge to the opening-side railing, with balusters along both.
 */
export function generateRailings(steps: IStepGeometry[], config: IStairConfig): IRailingGeometry[] {
    if (steps.length === 0) {
        return [];
    }
    const sides: ('left' | 'right')[] = config.railing.sides === 'both' ? ['left', 'right'] : [config.railing.sides];
    // The stair stands at the opening's left edge; the slab lies past an opening edge that is
    // not a room wall.
    const slab: ISlabSides = {
        pastExit: config.openingZ + config.openingLength < config.roomLength,
        left: config.openingX > 0,
        right: config.openingX + config.openingWidth < config.roomWidth,
    };
    return sides.map((side: 'left' | 'right') => buildSideRailing({ steps, config, side, slab }));
}
